import type { ChangeEvent } from 'react';
import { useEffect, useRef, useState } from 'react';

type State = {
  advancedOpen: boolean;
  department: string;
  ucid: string;
  location: string;
  phone: string;
  dept: Record<string, string>;
};

type Props = {
  state: State;
  toggleAdvancedSearch: () => void;
  handleChange: (event: ChangeEvent<HTMLInputElement>) => void;
  resetFilters: () => void;
  resetDept: () => void;
};

export default function AdvancedSearch({
  state,
  toggleAdvancedSearch,
  handleChange,
  resetFilters,
  resetDept,
}: Props) {
  const [deptLabel, setDeptLabel] = useState('');
  const removeRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    const label = state.dept[state.department];
    setDeptLabel(label ?? '');
  }, [state.department, state.dept]);

  useEffect(() => {
    if (deptLabel && removeRef.current) {
      removeRef.current.focus();
    }
  }, [deptLabel]);

  const removeClicked = () => {
    setDeptLabel('');
    resetDept();
  };

  const resetFilterClicked = () => {
    resetFilters();
    setDeptLabel('');
  };

  return (
    <aside className="degree-finder-facets people-client-facets">
      <div
        className={
          state.advancedOpen
            ? 'people-client-facets__panel'
            : 'people-client-facets__panel people-client-facets__panel--hidden'
        }
      >
        <div className="people-client-facets__row">
          <div className="form-element">
            <div className="label" id="people-org-label">
              Organization
            </div>
            <div className="people-client-facet-input-wrap people-client-facet-input-wrap--org">
              <input
                name="department"
                id="department"
                className="people-client-facet-field w-100"
                placeholder="-- Select or type --"
                type="text"
                onChange={handleChange}
                value={state.department}
                list="people-client-org-list"
                autoComplete="off"
                aria-labelledby="people-org-label"
              />
              {deptLabel ? (
                <div className="people-client-facets__dept-chip">
                  {deptLabel}
                  <a
                    className="people-client-facets__dept-remove"
                    href="#remove"
                    ref={removeRef}
                    tabIndex={0}
                    onClick={(e) => {
                      e.preventDefault();
                      removeClicked();
                    }}
                  >
                    ×
                  </a>
                </div>
              ) : null}
              <datalist id="people-client-org-list">
                {Object.entries(state.dept).map(([key, label]) => (
                  <option key={key} data-value={key} value={label} />
                ))}
              </datalist>
            </div>
          </div>

          <div className="form-element">
            <div className="label" id="people-ucid-label">
              UCID
            </div>
            <div className="people-client-facet-input-wrap">
              <input
                name="ucid"
                id="ucid"
                className="people-client-facet-field w-100"
                placeholder="UCID"
                type="text"
                onChange={handleChange}
                value={state.ucid}
                aria-labelledby="people-ucid-label"
              />
            </div>
          </div>

          <div className="form-element">
            <div className="label" id="people-location-label">
              Location
            </div>
            <div className="people-client-facet-input-wrap">
              <input
                name="location"
                id="location"
                className="people-client-facet-field w-100"
                placeholder="Location"
                type="text"
                onChange={handleChange}
                value={state.location}
                aria-labelledby="people-location-label"
              />
            </div>
          </div>

          <div className="form-element">
            <div className="label" id="people-phone-label">
              Official phone
            </div>
            <div className="people-client-facet-input-wrap">
              <input
                name="phone"
                id="phone"
                className="people-client-facet-field w-100"
                placeholder="Official phone"
                type="text"
                onChange={handleChange}
                value={state.phone}
                aria-labelledby="people-phone-label"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="people-client-facets__toolbar">
        <button type="button" className="people-client-facets__toggle" onClick={toggleAdvancedSearch}>
          <span className="people-client-facets__chevron" aria-hidden>
            {state.advancedOpen ? '▲' : '▼'}
          </span>
          Additional filters
        </button>
        <div className="people-client-facets__actions">
          <button type="button" className="people-client-facets__reset" onClick={resetFilterClicked}>
            Reset filters
          </button>
          <button type="submit" className="people-client-facets__submit">
            Search
          </button>
        </div>
      </div>
    </aside>
  );
}
